/**
 * steps/BasicSettingsStep.tsx
 * Basic settings configuration step for the onboarding wizard
 *
 * Collects user preferences:
 * - Display timezone
 * - Time format (12h/24h)
 * - Week start day (Sunday/Monday)
 * - Trading sessions (optional)
 *
 * Integrates with draft persistence system and follows wizard step pattern.
 */

// 1. External dependencies
import React, { useState, useEffect } from 'react';
import { Clock, Calendar, Info, Plus, Trash2 } from 'lucide-react';

// 2. Internal absolute imports
import {
  Input,
  Autocomplete,
  AutocompleteItem,
  RadioGroup,
  Radio,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Accordion,
  AccordionItem,
  TimeInput,
  CheckboxGroup,
  Checkbox,
  EmptyState,
} from '@enfomentis/ui';
import { Time } from '@internationalized/date';
import { TIMEZONES } from '@/constants/timezone';

// 3. Relative imports
import { useOnboardingData } from '../hooks/useOnboardingData';
import {
  getTimezoneAbbr,
  timeStringToUTC,
  timeStringFromUTC,
  formatTimeString
} from '@/utils/dateHelpers';

/**
 * Day of week type
 */
type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

/**
 * Day options for checkbox group
 */
const DAY_OPTIONS: Array<{ value: DayOfWeek; label: string }> = [
  { value: 'monday', label: 'Mon' },
  { value: 'tuesday', label: 'Tue' },
  { value: 'wednesday', label: 'Wed' },
  { value: 'thursday', label: 'Thu' },
  { value: 'friday', label: 'Fri' },
  { value: 'saturday', label: 'Sat' },
  { value: 'sunday', label: 'Sun' }
];

/**
 * Trading session interface
 */
interface TradingSession {
  name: string;
  description?: string;
  start: string;
  end: string;
  isActive: boolean;
  daysOfWeek?: DayOfWeek[];
}

/**
 * Basic settings step component
 */
export const BasicSettingsStep: React.FC = () => {
  const { draft, updatePersonal } = useOnboardingData();

  // Local form state
  const [formData, setFormData] = useState({
    displayTimezone: '',
    timeFormat: '24h' as '12h' | '24h',
    weekStartsOn: 'monday' as 'sunday' | 'monday',
    tradingSessions: [] as TradingSession[]
  });

  // Session editing state
  const [editingSession, setEditingSession] = useState<{
    index: number;
    name: string;
    description: string;
    start: Time;
    end: Time;
    daysOfWeek: string[];
  } | null>(null);

  // Initialize from draft
  useEffect(() => {
    if (draft?.personal) {
      setFormData({
        displayTimezone: draft.personal.displayTimezone || '',
        timeFormat: draft.personal.timeFormat || '24h',
        weekStartsOn: draft.personal.weekStartsOn || 'monday',
        tradingSessions: draft.personal.tradingSessions || []
      });
    }
  }, [draft]);

  /**
   * Update draft with current form data
   */
  const updateDraft = (updates: Partial<typeof formData>): void => {
    const updatedFormData = { ...formData, ...updates };
    setFormData(updatedFormData);

    updatePersonal({
      displayTimezone: updatedFormData.displayTimezone,
      timeFormat: updatedFormData.timeFormat,
      weekStartsOn: updatedFormData.weekStartsOn,
      tradingSessions: updatedFormData.tradingSessions
    });
  };

  /**
   * Handle timezone change
   */
  const handleTimezoneChange = (keys: 'all' | Set<React.Key>): void => {
    if (keys === 'all') return;
    const value = Array.from(keys)[0] as string;
    updateDraft({ displayTimezone: value });
  };

  /**
   * Handle time format change
   */
  const handleTimeFormatChange = (value: string): void => {
    updateDraft({ timeFormat: value as '12h' | '24h' });
  };

  /**
   * Handle week start change
   */
  const handleWeekStartChange = (value: string): void => {
    updateDraft({ weekStartsOn: value as 'sunday' | 'monday' });
  };

  /**
   * Add new session
   */
  const handleAddSession = (): void => {
    setEditingSession({
      index: -1, // -1 indicates new session
      name: '',
      description: '',
      start: new Time(9, 30),
      end: new Time(16, 0),
      daysOfWeek: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] // Default to Mon-Fri
    });
  };

  /**
   * Edit existing session
   */
  const handleEditSession = (index: number): void => {
    const session = formData.tradingSessions[index];
    if (session) {
      // Convert UTC times back to local timezone for editing
      setEditingSession({
        index,
        name: session.name,
        description: session.description || '',
        start: utcToLocal(session.start),
        end: utcToLocal(session.end),
        daysOfWeek: session.daysOfWeek || []
      });
    }
  };

  /**
   * Delete session
   */
  const handleDeleteSession = (index: number): void => {
    const updatedSessions = formData.tradingSessions.filter((_, i) => i !== index);
    updateDraft({ tradingSessions: updatedSessions });
  };

  /**
   * Convert Time object to UTC time string for storage
   * Times are stored in UTC in the database for consistency
   */
  const localToUTC = (time: Time): string => {
    if (!formData.displayTimezone) {
      // Fallback to storing as-is if no timezone set yet
      return `${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')}`;
    }

    const localTimeStr = `${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')}`;
    return timeStringToUTC(localTimeStr, formData.displayTimezone);
  };

  /**
   * Convert UTC time string to Time object for display/editing
   * Converts stored UTC times back to user's timezone
   */
  const utcToLocal = (utcTimeStr: string): Time => {
    if (!formData.displayTimezone) {
      // Fallback if no timezone
      const [hours, minutes] = utcTimeStr.split(':').map(Number);
      return new Time(hours || 0, minutes || 0);
    }

    const localTimeStr = timeStringFromUTC(utcTimeStr, formData.displayTimezone);
    const [hours, minutes] = localTimeStr.split(':').map(Number);
    return new Time(hours || 0, minutes || 0);
  };

  /**
   * Save editing session
   */
  const handleSaveSession = (): void => {
    if (!editingSession) return;

    // Convert times to UTC for storage
    const newSession: TradingSession = {
      name: editingSession.name,
      ...(editingSession.description && { description: editingSession.description }),
      start: localToUTC(editingSession.start),
      end: localToUTC(editingSession.end),
      isActive: true,
      ...(editingSession.daysOfWeek.length > 0 && { daysOfWeek: editingSession.daysOfWeek as DayOfWeek[] })
    };

    let updatedSessions: TradingSession[];
    if (editingSession.index === -1) {
      // New session
      updatedSessions = [...formData.tradingSessions, newSession];
    } else {
      // Edit existing
      updatedSessions = formData.tradingSessions.map((s, i) =>
        i === editingSession.index ? newSession : s
      );
    }

    updateDraft({ tradingSessions: updatedSessions });
    setEditingSession(null);
  };

  /**
   * Format time for display based on user's time format preference and timezone
   * Converts UTC time to local timezone and formats according to user preference
   */
  const formatTime = (utcTimeStr: string): string => {
    if (!formData.displayTimezone) {
      return utcTimeStr; // Fallback
    }

    return formatTimeString(utcTimeStr, formData.displayTimezone, formData.timeFormat);
  };

  /**
   * Get timezone abbreviation for display
   */
  const getTimezoneAbbrDisplay = (): string => {
    if (!formData.displayTimezone) return '';
    return getTimezoneAbbr(formData.displayTimezone);
  };

  /**
   * Determine which day preset is active
   */
  const getDayPreset = (days: string[]): 'mon-fri' | 'all' | 'custom' => {
    if (days.length === 0) return 'all';

    const monFri = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

    if (days.length === 7 && allDays.every(d => days.includes(d))) return 'all';
    if (days.length === 5 && monFri.every(d => days.includes(d))) return 'mon-fri';
    return 'custom';
  };

  /**
   * Handle day preset button click
   */
  const handleDayPreset = (preset: 'mon-fri' | 'all' | 'custom'): void => {
    if (!editingSession) return;

    if (preset === 'mon-fri') {
      setEditingSession({
        ...editingSession,
        daysOfWeek: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
      });
    } else if (preset === 'all') {
      setEditingSession({
        ...editingSession,
        daysOfWeek: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
      });
    }
    // For 'custom', do nothing - user will select manually
  };

  /**
   * Check if end time is after start time
   */
  const getTimeValidationError = (): string | null => {
    if (!editingSession) return null;

    const startMinutes = editingSession.start.hour * 60 + editingSession.start.minute;
    const endMinutes = editingSession.end.hour * 60 + editingSession.end.minute;

    if (endMinutes <= startMinutes) {
      return 'End time must be after start time';
    }

    return null;
  };

  /**
   * Check if editing session overlaps with existing sessions
   */
  const getOverlappingWarning = (): string | null => {
    if (!editingSession) return null;

    const editingDays = editingSession.daysOfWeek;
    const editingStart = editingSession.start.hour * 60 + editingSession.start.minute;
    const editingEnd = editingSession.end.hour * 60 + editingSession.end.minute;

    // Skip if no days selected (shouldn't happen due to validation)
    if (editingDays.length === 0) return null;

    // Check each existing session (skip the one being edited)
    for (let i = 0; i < formData.tradingSessions.length; i++) {
      if (i === editingSession.index) continue; // Skip self when editing

      const session = formData.tradingSessions[i];
      if (!session) continue;

      const sessionDays = session.daysOfWeek || [];

      // Check if sessions share any days
      const sharedDays = editingDays.filter((day) => sessionDays.includes(day as DayOfWeek));
      if (sharedDays.length === 0) continue; // No shared days, no overlap

      // Convert UTC session times to local for comparison
      const sessionStartLocal = utcToLocal(session.start);
      const sessionEndLocal = utcToLocal(session.end);
      const sessionStart = sessionStartLocal.hour * 60 + sessionStartLocal.minute;
      const sessionEnd = sessionEndLocal.hour * 60 + sessionEndLocal.minute;

      // Check for time overlap
      const hasTimeOverlap =
        (editingStart < sessionEnd && editingEnd > sessionStart) || // Standard overlap
        (editingStart === sessionStart && editingEnd === sessionEnd); // Exact match

      if (hasTimeOverlap) {
        const dayNames = sharedDays.map((d) => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(', ');
        return `Warning: This session overlaps with "${session.name}" on ${dayNames}`;
      }
    }

    return null;
  };

  /**
   * Check if form is valid
   */
  const isFormValid = (): boolean => {
    return !!(formData.displayTimezone && formData.timeFormat && formData.weekStartsOn);
  };

  return (
    <Card
      radius="none"
      shadow="none"
      className="h-full max-h-dvh w-full flex flex-col border-0"
    >
      <CardHeader>
        <div>
          <h3 className="text-large font-semibold text-text-primary">Basic Settings</h3>
          <p className="hidden sm:block text-sm text-text-secondary">Configure your timezone, time format, and trading schedule</p>
        </div>
      </CardHeader>

      <CardBody className="space-y-8">
        <div className="max-w-4xl mx-auto px-2">
          {/* Display Preferences Section */}
          <div className="space-y-4">
            <h3 className="text-md font-semibold text-text-secondary uppercase tracking-wide">
              Display Preferences
            </h3>

            {/* Timezone & Time Format - Side by side */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Timezone */}
              <div>
                <Autocomplete
                  label="Display Timezone"
                  labelPlacement="outside"
                  placeholder="Search timezones..."
                  selectedKey={formData.displayTimezone || null}
                  onSelectionChange={(key) => {
                    if (key) {
                      handleTimezoneChange(new Set([key]));
                    }
                  }}
                  isRequired
                  variant="bordered"
                  size="lg"
                  description="This timezone will be used throughout the app for all time displays"
                >
                  {TIMEZONES.map((tz) => (
                    <AutocompleteItem key={tz.value}>
                      {tz.label}
                    </AutocompleteItem>
                  ))}
                </Autocomplete>
              </div>

              {/* Time Format */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Time Format <span className="text-danger">*</span>
                </label>
                <RadioGroup
                  value={formData.timeFormat}
                  onValueChange={handleTimeFormatChange}
                  orientation="vertical"
                  size="lg"
                >
                  <Radio value="12h">12-hour (2:30 PM)</Radio>
                  <Radio value="24h">24-hour (14:30)</Radio>
                </RadioGroup>
              </div>
              {/* Week Start */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Week Starts On <span className="text-danger">*</span>
                </label>
                <RadioGroup
                  value={formData.weekStartsOn}
                  onValueChange={handleWeekStartChange}
                  orientation="vertical"
                  size="lg"
                >
                  <Radio value="sunday">Sunday</Radio>
                  <Radio value="monday">Monday</Radio>
                </RadioGroup>
              </div>
            
            </div>

            
          </div>

          {/* Trading Sessions Section */}
          <div className="space-y-4 pt-4">
            <div className="flex items-center justify-between">
              <h3 className="text-md font-semibold text-text-secondary uppercase tracking-wide">
                Trading Sessions (Optional)
              </h3>
              <Button
                size="sm"
                color="primary"
                variant="flat"
                startContent={<Plus size={16} />}
                onPress={handleAddSession}
              >
                Add Session
              </Button>
            </div>

            <p className="text-sm text-text-secondary">
              Defining your trading sessions helps with certain automations in the trading and analysis dashboards.
            </p>

            {/* Sessions List */}
            {formData.tradingSessions.length === 0 && !editingSession ? (
              <EmptyState
                icon={<Calendar className="w-12 h-12" />}
                title="No trading sessions defined"
                description="You'll be able to trade anytime"
              />
            ) : (
              <div className="space-y-2">
                {formData.tradingSessions.map((session, index) => (
                  <Card key={index} className="border border-default-200">
                    <CardBody className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-text-primary">{session.name}</div>
                          {session.description && (
                            <div className="text-sm text-text-secondary mt-1">{session.description}</div>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-sm text-text-secondary">
                            <span>
                              {formatTime(session.start)} - {formatTime(session.end)}
                              {getTimezoneAbbrDisplay() && <span className="text-xs ml-1">({getTimezoneAbbrDisplay()})</span>}
                            </span>
                            {session.daysOfWeek && session.daysOfWeek.length > 0 ? (
                              <span className="text-xs">
                                {session.daysOfWeek.map(d => d.slice(0, 3)).join(', ')}
                              </span>
                            ) : (
                              <span className="text-xs">All days</span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="light"
                            isIconOnly
                            onPress={() => handleEditSession(index)}
                          >
                            <Clock size={16} />
                          </Button>
                          <Button
                            size="sm"
                            variant="light"
                            color="danger"
                            isIconOnly
                            onPress={() => handleDeleteSession(index)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                ))}
              </div>
            )}

            {/* Session Editor */}
            {editingSession && (
              <Card className="border-2 border-primary">
                <CardHeader className="pb-2">
                  <h4 className="text-md font-semibold">
                    {editingSession.index === -1 ? 'Add Trading Session' : 'Edit Trading Session'}
                  </h4>
                </CardHeader>
                <CardBody className="space-y-4">
                  {/* Time Validation Error */}
                  {getTimeValidationError() && (
                    <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg">
                      <p className="text-sm text-danger-700">{getTimeValidationError()}</p>
                    </div>
                  )}

                  {/* Overlap Warning */}
                  {!getTimeValidationError() && getOverlappingWarning() && (
                    <div className="p-3 bg-warning-50 border border-warning-200 rounded-lg">
                      <p className="text-sm text-warning-700">{getOverlappingWarning()}</p>
                    </div>
                  )}
                  {/* Session Name */}
                  <Input
                    label="Session Name"
                    labelPlacement="outside"
                    placeholder="e.g., Morning Session, Friday Close"
                    value={editingSession.name}
                    onValueChange={(value) =>
                      setEditingSession({ ...editingSession, name: value })
                    }
                    isRequired
                    variant="bordered"
                    size="lg"
                  />

                  {/* Session Description */}
                  <Input
                    label="Description (Optional)"
                    labelPlacement="outside"
                    placeholder="Brief description"
                    value={editingSession.description}
                    onValueChange={(value) =>
                      setEditingSession({ ...editingSession, description: value })
                    }
                    variant="bordered"
                    size="lg"
                  />

                  {/* Time Range */}
                  <div className="grid grid-cols-2 gap-4">
                    <TimeInput
                      label={`Start Time${getTimezoneAbbrDisplay() ? ` (${getTimezoneAbbrDisplay()})` : ''}`}
                      labelPlacement="outside"
                      value={editingSession.start}
                      onChange={(value) => {
                        if (value && value instanceof Time) {
                          setEditingSession({ ...editingSession, start: value });
                        }
                      }}
                      hourCycle={formData.timeFormat === '12h' ? 12 : 24}
                      variant="bordered"
                      size="lg"
                    />
                    <TimeInput
                      label={`End Time${getTimezoneAbbrDisplay() ? ` (${getTimezoneAbbrDisplay()})` : ''}`}
                      labelPlacement="outside"
                      value={editingSession.end}
                      onChange={(value) => {
                        if (value && value instanceof Time) {
                          setEditingSession({ ...editingSession, end: value });
                        }
                      }}
                      hourCycle={formData.timeFormat === '12h' ? 12 : 24}
                      variant="bordered"
                      size="lg"
                    />
                  </div>

                  {/* Days of Week */}
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Days <span className="text-danger">*</span>
                      </label>
                      <p className="text-xs text-text-tertiary mb-2">Quick select or choose specific days</p>
                    </div>

                    {/* Quick Select Buttons */}
                    <ButtonGroup size="sm" variant="bordered" className="w-full">
                      <Button
                        className="flex-1"
                        color={getDayPreset(editingSession.daysOfWeek) === 'mon-fri' ? 'primary' : 'default'}
                        onPress={() => handleDayPreset('mon-fri')}
                      >
                        Mon-Fri
                      </Button>
                      <Button
                        className="flex-1"
                        color={getDayPreset(editingSession.daysOfWeek) === 'all' ? 'primary' : 'default'}
                        onPress={() => handleDayPreset('all')}
                      >
                        All Days
                      </Button>
                      <Button
                        className="flex-1"
                        color={getDayPreset(editingSession.daysOfWeek) === 'custom' ? 'primary' : 'default'}
                        onPress={() => handleDayPreset('custom')}
                      >
                        Custom
                      </Button>
                    </ButtonGroup>

                    {/* Day Checkboxes */}
                    <CheckboxGroup
                      orientation="horizontal"
                      value={editingSession.daysOfWeek}
                      onValueChange={(values) =>
                        setEditingSession({ ...editingSession, daysOfWeek: values as string[] })
                      }
                      isRequired
                      classNames={{
                        wrapper: "gap-2"
                      }}
                    >
                      {DAY_OPTIONS.map((day) => (
                        <Checkbox key={day.value} value={day.value}>
                          {day.label}
                        </Checkbox>
                      ))}
                    </CheckboxGroup>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 justify-end pt-2">
                    <Button
                      variant="light"
                      onPress={() => setEditingSession(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      color="primary"
                      onPress={handleSaveSession}
                      isDisabled={
                        !editingSession.name.trim() ||
                        editingSession.daysOfWeek.length === 0 ||
                        !!getTimeValidationError()
                      }
                    >
                      {editingSession.index === -1 ? 'Add Session' : 'Save Changes'}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            )}
          </div>

          {/* Validation Status */}
          {isFormValid() && (
            <div className="flex items-center gap-2 text-success text-sm">
              <div className="w-1.5 h-1.5 rounded-full bg-success"></div>
              Basic settings configured! Ready to continue.
            </div>
          )}
        </div>
      </CardBody>

      <CardFooter className="shrink-0 border-t border-default-200">
        <Accordion variant="light" fullWidth isCompact>
          <AccordionItem
            key="why-needed"
            textValue="Why do we need this?"
            title={
              <div className="flex items-center gap-2">
                <Info size={16} className="text-text-secondary" />
                <span className="text-sm font-medium text-text-secondary">
                  Why do we need this?
                </span>
              </div>
            }
          >
            <ul className="text-sm text-text-secondary space-y-1 pb-2">
              <li>• Display timezone affects how all times are shown throughout the app</li>
              <li>• Time format controls whether times display as 12-hour or 24-hour</li>
              <li>• Week start determines how calendars and weekly views are organized</li>
              <li>• Trading sessions help define your trading schedule (optional)</li>
              <li>• All settings can be changed later in Settings</li>
            </ul>
          </AccordionItem>
        </Accordion>
      </CardFooter>
    </Card>
  );
};
